import React from "react";
import toLower from "lodash/toLower";
import queryString from "query-string";
import ReCAPTCHA from "react-google-recaptcha";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import Styled from "@ats/src/views/sessions/components/Styled";
import { validateEmail } from "@shared/lib/validateWithYup";
import {
  adPlatformIdentifiers,
  posthogDistinctIdFromCookie,
  sanitizeTrackingParams,
} from "@shared/lib/utils";

import FormContainer from "@ats/src/components/forms/FormContainer";
import GoogleSSOButton from "@ats/src/views/sessions/components/GoogleSSOButton";
import SessionFormInput from "@ats/src/views/sessions/components/SessionFormInput";
import SessionButton from "@ats/src/views/sessions/components/SessionButton";

import { useMagicLink } from "@shared/queryHooks/useSession";
import { useGetInvite } from "@shared/queryHooks/useInvite";

function AuthForm({
  onComplete,
  location,
  inviteToken,
  darkModeAllowed = true,
}: {
  // eslint-disable-next-line no-unused-vars
  onComplete: ({ email }: { email: string }) => void;
  location: any;
  inviteToken?: string;
  darkModeAllowed?: boolean;
}) {
  const [state, setState] = React.useState({
    email: "",
    errors: [],
  });
  const path = queryString.parse(location.search).path;
  const redirectPath = path ? path : "/";
  // const inviteToken = queryString.parse(location.search).invite_token;
  const [referral, setReferral] = React.useState(queryString.parse(location.search).referral);
  const [partner] = React.useState(queryString.parse(location.search).partner);
  const [trackingParams] = React.useState(sanitizeTrackingParams(location.search));
  const [adPlatformValues] = React.useState(adPlatformIdentifiers(location.search));
  const recaptchaRef = React.useRef(null);

  const googleClickId = adPlatformValues.googleClickId
    ? adPlatformValues.googleClickId
    : adPlatformValues.googleFirstPartyCookie;
  const liFatId = adPlatformValues.liFatId
    ? adPlatformValues.liFatId
    : adPlatformValues.linkedinFirstPartyCookie;
  const posthogDistinctId = adPlatformValues.posthogDistinctId
    ? adPlatformValues.posthogDistinctId
    : posthogDistinctIdFromCookie();

  const { mutate: magicLink, isLoading: isLoadingMagicLink } = useMagicLink();
  const { data: inviteData } = useGetInvite(inviteToken);
  const inviteEmail = inviteData?.email;
  const { email, errors } = state;

  React.useEffect(() => {
    if (inviteEmail != undefined) {
      setState((state) => ({ ...state, email: inviteEmail }));
      setReferral("invite");
    }
  }, [inviteEmail]);

  const handleInputChange = (name, value) => {
    setState({ ...state, [name]: value, errors: null });
  };

  const handleAuth = async (e) => {
    e.preventDefault();

    // Trim email and update state
    const trimmedEmail = email.trim();
    setState({ ...state, email: trimmedEmail });

    const [isValid, validationErrors] = await validateEmail({ email: trimmedEmail });

    let cleanedEmail = toLower(trimmedEmail);

    if (isValid) {
      const recaptchaToken = window.IS_TEST_ENV ? null : await recaptchaRef.current.executeAsync();
      magicLink(
        {
          email: cleanedEmail,
          organizationSlug: null,
          inviteToken,
          redirectTo: redirectPath,
          referral,
          partner,
          utmSource: trackingParams.utmSource,
          utmCampaign: trackingParams.utmCampaign,
          utmData: trackingParams.utmData,
          internalRef: trackingParams.internalRef,
          adrollClickId: adPlatformValues.adrollClickId,
          gaClientId: adPlatformValues.gaClientId,
          gaSessionId: adPlatformValues.gaSessionId,
          fbclid: adPlatformValues.fbclid,
          fbp: adPlatformValues.fbp,
          fbc: adPlatformValues.fbc,
          liFatId: liFatId,
          googleClickId: googleClickId,
          adrollFirstPartyCookie: adPlatformValues.adrollFirstPartyCookie,
          posthogDistinctId: posthogDistinctId,
          loginIntent: "hire",
          recaptchaToken: recaptchaToken,
        },
        {
          onSuccess: (data) => {
            window.logger("[AuthForm] data", { ...data });
            onComplete({ email: cleanedEmail, ...data });
          },
          onError: (response: any) => {
            window.logger("[AuthForm] server error", { response });
            // Handles Devise Errors
            if (response.data.error) {
              setState({ ...state, errors: [response.data.error] });
            }
            // Handles non Devise Errors
            if (response.data.errors) {
              setState({ ...state, errors: response.data.errors });
            }
          },
        },
      );
      recaptchaRef?.current?.reset();
    } else {
      window.logger("[AuthForm] errors", { validationErrors });
      setState({ ...state, errors: validationErrors });
    }
  };

  const recaptchaElement = (
    <Styled.RecaptchaContainer>
      <ReCAPTCHA
        ref={recaptchaRef}
        size="invisible"
        sitekey={(window as any).GOOGLE_RECAPTCHA_SITE_KEY}
        className="grecaptcha-badge"
      />
    </Styled.RecaptchaContainer>
  );

  return (
    <Styled.AuthForm>
      <Styled.GoogleSSOWrapper>
        <GoogleSSOButton
          referral={referral}
          partner={partner}
          utmSource={trackingParams.utmSource}
          utmCampaign={trackingParams.utmCampaign}
          utmData={trackingParams.utmData}
          internalRef={trackingParams.internalRef}
          adrollClickId={adPlatformValues.adrollClickId}
          gaClientId={adPlatformValues.gaClientId}
          gaSessionId={adPlatformValues.gaSessionId}
          fbclid={adPlatformValues.fbclid}
          fbp={adPlatformValues.fbp}
          fbc={adPlatformValues.fbc}
          liFatId={liFatId}
          googleClickId={googleClickId}
          adrollFirstPartyCookie={adPlatformValues.adrollFirstPartyCookie}
          posthogDistinctId={posthogDistinctId}
          darkModeAllowed={darkModeAllowed}
        />
      </Styled.GoogleSSOWrapper>
      <Local.ContinueText darkModeAllowed={darkModeAllowed}>Or continue with email</Local.ContinueText>

      <FormContainer
        onSubmit={handleAuth}
        errors={errors}
        buttons={
          <SessionButton type="submit" disabled={isLoadingMagicLink} loading={isLoadingMagicLink} darkModeAllowed={darkModeAllowed}>
            Continue with email
          </SessionButton>
        }
      >
        <SessionFormInput
          onChange={handleInputChange}
          name="email"
          placeholder=""
          label="Email"
          className=""
          value={email || ""}
          errors={errors}
          isDisabled={inviteToken != undefined}
          darkModeAllowed={darkModeAllowed}
        />
        {recaptchaElement}
      </FormContainer>
    </Styled.AuthForm>
  );
}

export default AuthForm;

/* Local styled components */
const Local: any = {};

Local.ContinueText = styled.div((props: any) => {
  const t: any = props.theme;
  const darkModeAllowed = props.darkModeAllowed;
  return css`
    text-align: center; 
    ${[t.pb(6), t.pt(5)]};
    color: ${darkModeAllowed && t.dark ? t.color.gray[300] : t.color.gray[600]};
  `;
});
